import {
  collection,
  collectionGroup,
  doc,
  writeBatch,
  type FirestoreDataConverter,
} from 'firebase/firestore'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { addDocWithError } from '../../firestore-mobx/fetch'
import { serverTimestamp } from 'firebase/firestore'
import { type FirestoreRoomStateReset, schema } from './schema'
import { buildUnsupportedConverter } from '../shared/noops'
export * from './schema'
import {
  modelItemStream,
  modelListStream,
  partitionedQueryStream,
} from '../../firestore-mobx/stream'
import { RoomStateReset } from '../../models/RoomStateReset'

const readConverter: FirestoreDataConverter<FirestoreRoomStateReset> = {
  toFirestore: buildUnsupportedConverter<FirestoreRoomStateReset>().toFirestore,
  fromFirestore: (snapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    return schema.parse(data)
  },
}

// const writeConverter: FirestoreDataConverter<z.infer<typeof writeSchema>> = {
//   toFirestore: (data) => {
//     writeSchema.partial().parse(data)
//     return data
//   },
//   fromFirestore:
//     buildUnsupportedConverter<z.infer<typeof writeSchema>>().fromFirestore,
// }

export const resetRoomState = async (
  repository: FirebaseRepository,
  {
    roomId,
    sectionId,
    assignmentId,
    timeout,
    userId,
  }: {
    roomId: string
    sectionId: string
    assignmentId: string
    timeout?: number
    userId?: string
  }
): Promise<void> => {
  const resetColRef = collection(
    repository.firestore,
    'room_state',
    roomId,
    'room_state_reset'
  )

  const ref = await addDocWithError(resetColRef, {
    endedAt: null,
    roomId: roomId,
    startedAt: serverTimestamp(),
    assignmentId: assignmentId,
    sectionId: sectionId,
    userId: userId ?? repository.auth.currentUser?.uid,
  })

  const stream = modelItemStream(
    repository,
    ref.withConverter(readConverter),
    RoomStateReset
  )

  let timeoutId: NodeJS.Timeout | undefined

  const errorAfterTimeout = new Promise(
    (_, reject) =>
      (timeoutId = setTimeout(
        () => reject(new Error('timeout waiting for room state reset')),
        timeout || 10000
      ))
  )

  try {
    await Promise.race([
      stream.firstWhere((doc) => doc.data.success === true),
      errorAfterTimeout,
    ])
  } finally {
    stream.close()
    clearTimeout(timeoutId)
  }
}

export const resetRoomStates = async (
  repository: FirebaseRepository,
  {
    roomIds,
    sectionId,
    assignmentId,
    timeout,
  }: {
    roomIds: string[]
    sectionId: string
    assignmentId: string
    timeout?: number
  }
): Promise<void> => {
  if (!roomIds.length) return
  if (roomIds.length === 1) {
    return resetRoomState(repository, {
      roomId: roomIds[0],
      sectionId,
      assignmentId,
      timeout,
    })
  }

  const resetDocIds: string[] = []

  for (const roomIdChunk of chunk(roomIds, ROOMS_PER_BATCH)) {
    const batch = writeBatch(repository.firestore)

    for (const roomId of roomIdChunk) {
      const resetColRef = collection(
        repository.firestore,
        'room_state',
        roomId,
        'room_state_reset'
      )

      // make new doc ref
      const newDocRef = doc(resetColRef)

      batch.set(newDocRef, {
        endedAt: null,
        roomId: roomId,
        startedAt: serverTimestamp(),
        assignmentId: assignmentId,
        sectionId: sectionId,
        userId: repository.auth.currentUser?.uid,
      })

      // collection group documentId queries must be fully qualified paths
      resetDocIds.push(`room_state/${roomId}/room_state_reset/${newDocRef.id}`)
    }

    // Validate and write the data in one operation
    await batch.commit()
  }

  // get a stream of all the new docs
  const stream = partitionedQueryStream({
    repository,
    matchArray: resetDocIds,
    ref: collectionGroup(
      repository.firestore,
      'room_state_reset'
    ).withConverter(readConverter),
    model: RoomStateReset,
  })

  let timeoutId: NodeJS.Timeout | undefined

  const errorAfterTimeout = new Promise(
    (_, reject) =>
      (timeoutId = setTimeout(
        () => reject(new Error('timeout waiting for room state reset')),
        timeout || 10000
      ))
  )

  try {
    // wait for all the docs to be marked as successful
    await Promise.race([
      stream.firstWhere((docs) => {
        return docs.every((doc) => doc.data.success === true)
      }),
      errorAfterTimeout,
    ])
  } finally {
    stream.close()
    clearTimeout(timeoutId)
  }
}

export const deleteRoomState = async (
  repository: FirebaseRepository,
  {
    roomId,
    sectionId,
    assignmentId,
  }: {
    roomId: string
    sectionId: string
    assignmentId: string
  }
): Promise<void> => {
  await deleteRoomStates(repository, [{ roomId, sectionId, assignmentId }])
}

export const deleteRoomStates = async (
  repository: FirebaseRepository,
  rooms: Array<{
    roomId: string
    sectionId: string
    assignmentId: string
  }>
): Promise<void> => {
  for (const roomChunk of chunk(rooms, ROOMS_PER_BATCH)) {
    const batch = writeBatch(repository.firestore)

    roomChunk.forEach(({ roomId, sectionId, assignmentId }) => {
      const resetColRef = collection(
        repository.firestore,
        'room_state',
        roomId,
        'room_state_reset'
      )

      const newDocRef = doc(resetColRef)
      batch.set(newDocRef, {
        endedAt: null,
        roomId: roomId,
        startedAt: serverTimestamp(),
        assignmentId: assignmentId,
        sectionId: sectionId,
        userId: repository.auth.currentUser?.uid,
        deleteRoomState: true,
      })
    })

    await batch.commit()
  }
}

// Security rules may make up to 20 document access calls per batched write, and
// the room_state_reset create rule reads each room to bind its sectionId. Those
// reads are one per room because the documents differ; the shared profile and
// section reads are cached. Ten leaves headroom for them, so "reset all" on a
// large assignment commits in several batches instead of failing as one.
// https://firebase.google.com/docs/firestore/quotas#security_rules
//
// The batches commit one at a time, so unlike the single commit this replaced,
// a rejection part-way leaves the earlier rooms already reset or deleted. Both
// callers (InstructorAssignmentCubit.resetAllGroups and .clearEmptyGroups) are
// buttons the instructor presses again, and they rebuild their room list from
// the live room_state stream: a second press re-resets rooms that already
// succeeded, which is what pressing reset twice does anyway, and skips the
// groups that were already deleted.
const ROOMS_PER_BATCH = 10

function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = []
  for (let startIndex = 0; startIndex < items.length; startIndex += size) {
    chunks.push(items.slice(startIndex, startIndex + size))
  }
  return chunks
}

export const getRoomStateResets = (
  repository: FirebaseRepository,
  { roomId }: { roomId: string }
) => {
  const resetColRef = collection(
    repository.firestore,
    'room_state',
    roomId,
    'room_state_reset'
  ).withConverter(readConverter)

  return modelListStream(repository, resetColRef, RoomStateReset)
}
